import { jsx, jsxs } from 'react/jsx-runtime';
import { Flex, themedColor, font } from '@lemonade-hq/cdk';
import { LOCALES_PER_REGION, Product, US_REGION } from '@lemonade-hq/lemonation';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis';
import { rgba } from 'polished';
import { useMemo } from 'react';
import { useCookies } from 'react-cookie';
import styled from 'styled-components';
import { ApiRoute } from '../../config/api.esm.js';
import { getFooterLinksByCountryAndRegion } from '../../helpers/links.esm.js';
import { useCommonI18nContext } from '../../providers/I18nProvider.esm.js';
import { AlternativeLinks } from './internals/AlternativeLinks.esm.js';
import { ContactDetails } from './internals/ContactDetails.esm.js';
import { DownloadLinks } from './internals/DownloadLinks.esm.js';
import { FooterContentLinks } from './internals/FooterContentLinks.esm.js';
import { RegionSelect } from './internals/RegionSelect.esm.js';
import { SocialLinks } from './internals/SocialLinks.esm.js';
import { Cookie } from '../../config/cookies.esm.js';

/* eslint-disable @typescript-eslint/naming-convention */ function _array_like_to_array(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _array_with_holes(arr) {
    if (Array.isArray(arr)) return arr;
}
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _iterable_to_array_limit(arr, i) {
    var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
    if (_i == null) return;
    var _arr = [];
    var _n = true;
    var _d = false;
    var _s, _e;
    try {
        for(_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true){
            _arr.push(_s.value);
            if (i && _arr.length === i) break;
        }
    } catch (err) {
        _d = true;
        _e = err;
    } finally{
        try {
            if (!_n && _i["return"] != null) _i["return"]();
        } finally{
            if (_d) throw _e;
        }
    }
    return _arr;
}
function _non_iterable_rest() {
    throw new TypeError("Invalid attempt to destructure non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
function _sliced_to_array(arr, i) {
    return _array_with_holes(arr) || _iterable_to_array_limit(arr, i) || _unsupported_iterable_to_array(arr, i) || _non_iterable_rest();
}
function _tagged_template_literal(strings, raw) {
    if (!raw) {
        raw = strings.slice(0);
    }
    return Object.freeze(Object.defineProperties(strings, {
        raw: {
            value: Object.freeze(raw)
        }
    }));
}
function _unsupported_iterable_to_array(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _array_like_to_array(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _array_like_to_array(o, minLen);
}
function _templateObject() {
    var data = _tagged_template_literal([
        "\n  background-color: ",
        ";\n  text-size-adjust: none;\n  -moz-osx-font-smoothing: grayscale;\n  text-wrap: pretty;\n"
    ]);
    _templateObject = function _templateObject() {
        return data;
    };
    return data;
}
function _templateObject1() {
    var data = _tagged_template_literal([
        "\n  max-width: 1246px;\n"
    ]);
    _templateObject1 = function _templateObject1() {
        return data;
    };
    return data;
}
function _templateObject2() {
    var data = _tagged_template_literal([
        "\n  max-width: 1246px;\n  gap: 2.1rem;\n  ",
        ";\n  color: ",
        ";\n  white-space: pre-wrap;\n"
    ]);
    _templateObject2 = function _templateObject2() {
        return data;
    };
    return data;
}
function _templateObject3() {
    var data = _tagged_template_literal([
        "\n  color: ",
        ";\n  ",
        ";\n"
    ]);
    _templateObject3 = function _templateObject3() {
        return data;
    };
    return data;
}
function _templateObject4() {
    var data = _tagged_template_literal([
        "\n    border-top: ",
        ";\n  "
    ]);
    _templateObject4 = function _templateObject4() {
        return data;
    };
    return data;
}
function _templateObject5() {
    var data = _tagged_template_literal([
        "\n  border-top: none;\n  ",
        "\n"
    ]);
    _templateObject5 = function _templateObject5() {
        return data;
    };
    return data;
}
var FooterContainer = styled(Flex)(_templateObject(), themedColor("darkestNight"));
var LinksContainer = styled(Flex)(_templateObject1());
var DisclaimerContainer = styled(Flex)(_templateObject2(), font("main", {
    fontSize: "1.4rem",
    lineHeight: "2.1rem"
}), function(props) {
    return rgba(props.theme.colors.metal, 0.5);
});
var Address = styled.p(_templateObject3(), themedColor("metal"), font("main", {
    fontSize: "1.4rem",
    lineHeight: "2.1rem"
}));
var SecondarySectionWrapper = styled(Flex)(_templateObject5(), MEDIA_QUERIES.tabletAndDesktop(_templateObject4(), function(props) {
    return "solid 1px ".concat(rgba(props.theme.colors.dividerGray, 0.5));
}));
var address = {
    US: "Lemonade Insurance, 5 Crosby St. 3rd floor, New York, NY 10013",
    NL: "Lemonade Agency B.V. Spuistraat 112A 1012 VA Amsterdam, Netherlands",
    DE: "Lemonade Agency B.V. Spuistraat 112A 1012 VA Amsterdam, Netherlands",
    FR: "Lemonade Agency B.V. Spuistraat 112A 1012 VA Amsterdam, Netherlands",
    GB: "Lemonade Insurance N.V., UK Branch, 51 Eastcheap, London, EC3M 1DT "
};
var Footer = function(_param) {
    var links = _param.links, _param_localeChangeRedirectsWithSuffixes = _param.localeChangeRedirectsWithSuffixes, localeChangeRedirectsWithSuffixes = _param_localeChangeRedirectsWithSuffixes === void 0 ? true : _param_localeChangeRedirectsWithSuffixes, _param_localeChangeUrl = _param.localeChangeUrl, localeChangeUrl = _param_localeChangeUrl === void 0 ? ApiRoute.UpdateLocale : _param_localeChangeUrl, _param_localeChangeLanguageOnly = _param.localeChangeLanguageOnly, localeChangeLanguageOnly = _param_localeChangeLanguageOnly === void 0 ? false : _param_localeChangeLanguageOnly, routerHoc = _param.routerHoc, _param_showDisclaimer = _param.showDisclaimer, showDisclaimer = _param_showDisclaimer === void 0 ? true : _param_showDisclaimer, _param_showDownloadLinks = _param.showDownloadLinks, showDownloadLinks = _param_showDownloadLinks === void 0 ? true : _param_showDownloadLinks, product = _param.product, extraDisclaimer = _param.extraDisclaimer, contactDetails = _param.contactDetails, props = _object_without_properties(_param, [
        "links",
        "localeChangeRedirectsWithSuffixes",
        "localeChangeUrl",
        "localeChangeLanguageOnly",
        "routerHoc",
        "showDisclaimer",
        "showDownloadLinks",
        "product",
        "extraDisclaimer",
        "contactDetails"
    ]);
    var _useCommonI18nContext = useCommonI18nContext(), t = _useCommonI18nContext.t, region = _useCommonI18nContext.region;
    var RegionSelectComponent = useMemo(function() {
        return routerHoc != null ? routerHoc(RegionSelect) : RegionSelect;
    }, [
        routerHoc
    ]);
    var _useCookies = _sliced_to_array(useCookies([
        Cookie.Geography
    ]), 1), cookies = _useCookies[0];
    var resolvedLinks = useMemo(function() {
        return links !== null && links !== void 0 ? links : getFooterLinksByCountryAndRegion(cookies[Cookie.Geography]);
    }, [
        links,
        cookies
    ]);
    var shouldHideRegionSelect = localeChangeLanguageOnly && LOCALES_PER_REGION[region].length < 2;
    var showContactDetails = region === US_REGION;
    return /*#__PURE__*/ jsx(FooterContainer, _object_spread_props(_object_spread({
        alignItems: "center",
        as: "footer",
        flexDirection: "column",
        px: [
            "20px",
            "20px",
            "60px"
        ],
        py: [
            "24px",
            "40px",
            "60px"
        ]
    }, props), {
        children: /*#__PURE__*/ jsxs(LinksContainer, {
            flexWrap: "wrap",
            width: 1,
            children: [
                /*#__PURE__*/ jsx(FooterContentLinks, {
                    links: resolvedLinks,
                    product: product !== null && product !== void 0 ? product : Product.Car
                }),
                /*#__PURE__*/ jsxs(Flex, {
                    alignItems: [
                        "center",
                        "center",
                        "flex-end"
                    ],
                    flexWrap: "wrap",
                    gap: "20px",
                    justifyContent: "space-between",
                    mb: [
                        "10px",
                        "20px"
                    ],
                    mt: [
                        "30px",
                        "10px",
                        "60px"
                    ],
                    width: "100%",
                    children: [
                        /*#__PURE__*/ jsxs(Flex, {
                            alignItems: [
                                "flex-start",
                                "center"
                            ],
                            flexDirection: [
                                "column",
                                "row"
                            ],
                            gap: "20px",
                            mb: [
                                "20px",
                                "0px"
                            ],
                            children: [
                                /*#__PURE__*/ jsx(SocialLinks, {}),
                                showContactDetails && /*#__PURE__*/ jsx(ContactDetails, {
                                    contactDetails: contactDetails
                                })
                            ]
                        }),
                        /*#__PURE__*/ jsxs(Flex, {
                            alignItems: "center",
                            flexDirection: [
                                "column",
                                "row"
                            ],
                            gap: "12px",
                            width: [
                                "100%",
                                "auto"
                            ],
                            children: [
                                !shouldHideRegionSelect && /*#__PURE__*/ jsx(RegionSelectComponent, {
                                    localeChangeLanguageOnly: localeChangeLanguageOnly,
                                    localeChangeRedirectsWithSuffixes: localeChangeRedirectsWithSuffixes,
                                    localeChangeUrl: localeChangeUrl
                                }),
                                showDownloadLinks && /*#__PURE__*/ jsx(DownloadLinks, {})
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ jsxs(SecondarySectionWrapper, {
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "16px",
                    pt: "20px",
                    width: "100%",
                    children: [
                        /*#__PURE__*/ jsx(Address, {
                            children: address[region]
                        }),
                        /*#__PURE__*/ jsx(AlternativeLinks, {})
                    ]
                }),
                showDisclaimer && /*#__PURE__*/ jsxs(DisclaimerContainer, {
                    flexDirection: "column",
                    mt: "20px",
                    children: [
                        /*#__PURE__*/ jsx("p", {
                            children: t("footer.disclaimer")
                        }),
                        extraDisclaimer
                    ]
                })
            ]
        })
    }));
};

export { Footer };
